import { useEffect, useMemo } from 'react'

import { useFetchObjectDataWithFixedKey } from '@components/DisplayForm/hooks/useFetchObjectDataWithFixedKey'
import { useFetchPreFillObjectDataWithFixedKey } from '@components/DisplayForm/hooks/useFetchPreFillObjectDataWithFixedKey'

import { FIELD_VALUE_TYPE, FORM_TYPE, PreFillType } from '@constants'

type UseFetchRowProps = {
  // objectCode: string
  type: FORM_TYPE
  formCode: string
  objectId?: string
  globalId: string
  preFill?: PreFillType
  hasPreFillField: boolean
  isFormReady: boolean
  typeId?: FIELD_VALUE_TYPE
  // fetchRow: MutationType<GETObjectDataRecordParams, ObjectDataRecord>
}

export const useFetchRow = ({
  type,
  objectId,
  formCode,
  globalId,
  preFill,
  hasPreFillField,
  isFormReady,
  typeId,
}: UseFetchRowProps) => {
  const [fetchRow, { isLoading: isLoadingRow, data }] = useFetchObjectDataWithFixedKey(
    globalId,
    typeId
  )
  const [fetchPreFillRow, { isLoading: isLoadingPreFillRow, data: preFillData }] =
    useFetchPreFillObjectDataWithFixedKey(globalId)

  const preFillObjectId = preFill?.objectId
  const preFillObjectCode = preFill?.objectCode

  useEffect(() => {
    // Ждем пока форма загрузится
    if (!isFormReady) {
      return
    }

    if (!formCode) {
      return
    }

    // Если есть preFill поля, загружаем данные для предварительного заполнения
    if (hasPreFillField && type === FORM_TYPE.CREATE) {
      fetchPreFillRow({
        formCode,
        objectId: preFillObjectId,
        objectCode: preFillObjectCode,
      })

      return
    }

    // Иначе загружаем данные как обычно (для формы редактирования)
    if (objectId) {
      fetchRow({ formCode, objectId })

      return
    }
  }, [formCode, objectId, preFillObjectId, preFillObjectCode, hasPreFillField, type, isFormReady])

  // Определяем, требуется ли начальная загрузка данных
  // Для форм редактирования (когда есть objectId) - изначально должна быть загрузка
  // Для форм создания с preFill - аналогично
  const needsInitialLoad = useMemo(() => {
    if (type === FORM_TYPE.CREATE) {
      return hasPreFillField && Boolean(preFillObjectId)
    }

    return !!objectId
  }, [type, objectId, hasPreFillField, preFillObjectId])

  // Для форм создания с preFill используем preFillData
  // Для форм редактирования ВСЕГДА используем обычные data (даже если hasPreFillField = true)
  const shouldUsePreFillData =
    type === FORM_TYPE.CREATE && hasPreFillField && Boolean(preFillObjectId)

  const resultIsLoadingRow = shouldUsePreFillData ? isLoadingPreFillRow : isLoadingRow
  const resultRow = shouldUsePreFillData ? preFillData : data

  // Если требуется начальная загрузка, но данных еще нет - показываем состояние загрузки
  // Это предотвращает мерцание пустой формы при первом рендере
  const hasData = !!resultRow
  const shouldShowLoading = needsInitialLoad && !hasData
  const finalIsLoadingRow = shouldShowLoading || resultIsLoadingRow

  return {
    isLoadingRow: finalIsLoadingRow,
    row: resultRow,
  }
}
